/*
  The two moving parts of the homepage.

  Everything here is an enhancement of markup that already works: the
  hero shows its poster, and the routine finder's button goes to the
  collection whatever is chosen. If this file never loads, the page loses
  a film and the finder's answers, and stays usable.

  No libraries, no build step, and no work on scroll.
*/
(() => {
  // Every landing section asks for this file, so the browser runs it once
  // per section tag. The work below is page-wide and idempotent only if it
  // happens once, so the first run claims the page.
  if (window.hzfLanding) return;
  window.hzfLanding = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* -------------------------------------------------------------------
     The wallpaper film.

     The policy is: play only while the hero is on screen, the tab is in
     front and motion is welcome. Anything else pauses. The source is chosen at the same breakpoint as
     the poster, so a phone never fetches the desktop cut, and it is only
     set once the film is actually going to play, which is what keeps
     `preload="none"` honest.
  ------------------------------------------------------------------- */
  const hero = document.querySelector('[data-hzf-wall]');
  const video = hero && hero.querySelector('[data-hzf-wall-video]');

  if (hero && video) {
    const narrow = window.matchMedia('(max-width: 860px)');
    let visible = false;
    let broken = false;

    const sync = () => {
      const play = !reduced.matches && !broken && visible && !document.hidden;

      if (!play) {
        video.pause();
        return;
      }

      const source = narrow.matches ? video.dataset.srcMobile : video.dataset.srcDesktop;

      if (video.getAttribute('src') !== source) {
        video.classList.remove('is-ready');
        video.setAttribute('src', source);
        video.load();
      }

      video.muted = true;
      const started = video.play();
      if (started) started.catch(() => {});
    };

    video.addEventListener('playing', () => {
      broken = false;
      video.classList.add('is-ready');
    });

    // One failure is enough: a file that will not decode here will not
    // decode on the next scroll either, and retrying would keep asking.
    video.addEventListener('error', () => {
      broken = true;
      video.classList.remove('is-ready');
    });

    new IntersectionObserver(
      (entries) => {
        visible = entries[0].isIntersecting;
        sync();
      },
      { threshold: 0 }
    ).observe(hero);

    document.addEventListener('visibilitychange', sync);
    narrow.addEventListener('change', sync);
    reduced.addEventListener('change', sync);
  }

  /* -------------------------------------------------------------------
     The routine finder.

     Three options, one answer, and a link that goes to the collection
     with that category already chosen. The answer text lives in the
     markup, so the choice only moves it into view.
  ------------------------------------------------------------------- */
  document.querySelectorAll('[data-hzf-finder]').forEach((finder) => {
    const options = finder.querySelectorAll('[data-hzf-need]');
    const answer = finder.querySelector('[data-hzf-answer]');
    const cta = finder.querySelector('[data-hzf-finder-cta]');
    const ctaText = finder.querySelector('[data-hzf-finder-cta-text]');

    options.forEach((option) => {
      option.addEventListener('click', () => {
        const chosen = option.getAttribute('aria-pressed') !== 'true';

        options.forEach((other) => other.setAttribute('aria-pressed', String(other === option && chosen)));

        if (answer) {
          answer.textContent = chosen ? option.dataset.answer : answer.dataset.default;
        }

        if (cta) {
          cta.setAttribute('href', chosen ? option.dataset.href : cta.dataset.defaultHref);
          if (ctaText) {
            ctaText.textContent = chosen ? cta.dataset.labelChosen : cta.dataset.labelDefault;
          }
        }
      });
    });
  });
})();
